<script setup>
import { stack, closePanel, finalisePanel, markPanelArrived } from '@/shared/panelStack.js';
import PopOutPanel from '@/components/PopOutPanel.vue';

// Renders the pop-out panel stack. Mounted exactly once per page (see
// resources/js/panels/main.js) - a second host would render every panel twice.
//
// Teleported to <body> so these fixed-position panels cannot be trapped inside the
// stacking context of whatever mounted the host, and so they sit where the Blade
// component's own markup sits (a top-level sibling, included after </header>).

// A panel is covered only when something above it has *finished arriving*. Two timing
// traps, one at each end:
//
// - A panel that has been pushed but is still sliding in is not covering anything yet,
//   so hiding the one below on push leaves the page bare for the length of the slide.
//   Hence `arrived`, set when the incoming panel's transition ends.
// - A closing panel stops counting immediately, so the one beneath is revealed as it
//   slides away rather than only once it unmounts, which would show the same gap in
//   reverse.
function isCovered(index) {
    return stack.slice(index + 1).some(entry => entry.arrived && !entry.closing);
}

// Only the bottom of the stack renders a visible overlay - see PopOutPanel.vue's
// showOverlay prop. One constant backdrop for the whole stack, rather than each panel
// fading its own in and out over the top of the last.
//
// Note this is why `close` is not handled per-entry below. The overlay a user clicks
// belongs to the *bottom* panel, so closing the emitting entry would peel the stack
// from the wrong end: with three open, clicking outside would shut panel 1 while panel
// 3 stayed on screen. A close gesture always means the top of the stack, wherever it
// came from - and since covered panels are visibility: hidden, and so cannot be
// clicked, the close button can only ever be the top panel's anyway.
function showsOverlay(index) {
    return index === 0;
}
</script>

<template>
    <Teleport to="body">
        <PopOutPanel
            v-for="(entry, index) in stack"
            :key="entry.key"
            :title="entry.title"
            :title-class="entry.titleClass"
            :covered="isCovered(index)"
            :closing="entry.closing"
            :content-version="entry.version"
            :show-overlay="showsOverlay(index)"
            @opened="markPanelArrived(entry.key)"
            @close="closePanel()"
            @closed="finalisePanel(entry.key)"
        >
            <component :is="entry.component" v-bind="entry.props" />
        </PopOutPanel>
    </Teleport>
</template>
